import { loadTossPayments, type TossPaymentsWidgets } from '@tosspayments/tosspayments-sdk';
import { supabase } from '@/lib/supabase';

// TossPayments 타입 정의
export interface PaymentRequest {
  orderId: string;
  orderName: string;
  amount: number;
  customerEmail?: string;
  customerName?: string;
  customerMobilePhone?: string;
  cartId: string;
  checkoutToken: string;
  orderType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  restaurantId: string;
  userId?: string;
}

export interface PaymentConfirmation {
  paymentKey: string;
  orderId: string;
  amount: number;
}

export interface PaymentCancellation {
  paymentKey: string;
  cancelReason: string;
  cancelAmount?: number; // 부분 취소 시 금액
}

export interface RefundRequest {
  paymentKey: string;
  cancelReason: string;
  cancelAmount: number;
  refundReceiveAccount?: {
    bank: string;
    accountNumber: string;
    holderName: string;
  };
}

class TossPaymentsService {
  private clientKey: string;
  private secretKey: string;
  private widgets: TossPaymentsWidgets | null = null;
  private isInitialized = false;

  constructor() {
    this.clientKey = import.meta.env.VITE_TOSSPAYMENTS_CLIENT_KEY || '';
    this.secretKey = import.meta.env.VITE_TOSSPAYMENTS_SECRET_KEY || '';
    
    if (!this.clientKey || !this.secretKey) {
      console.error('🚨 TossPayments API keys are not configured');
      return;
    }
    
    // API 키 형식 검증 및 경고
    this.validateApiKeyFormat();
  }

  /**
   * API 키 형식 검증
   */
  private validateApiKeyFormat(): void {
    const isTestClient = this.clientKey.startsWith('test_gck_');
    const isTestSecret = this.secretKey.startsWith('test_gsk_');
    const isLiveClient = this.clientKey.startsWith('live_gck_');
    const isLiveSecret = this.secretKey.startsWith('live_gsk_');
    
    // API 키 형식 검증 (에러 로깅만 유지)
    if (!isTestClient && !isLiveClient) {
      console.warn('⚠️ 클라이언트 키가 결제위젯 연동 키 형식이 아닙니다');
    }
    
    if (!isTestSecret && !isLiveSecret) {
      console.warn('⚠️ 시크릿 키가 결제위젯 연동 키 형식이 아닙니다');
    }
    
    // 키 쌍 일치 확인 (에러만 로깅)
    if (isTestClient && !isTestSecret) {
      console.error('🚨 테스트 클라이언트 키와 시크릿 키가 매칭되지 않습니다');
    }
    
    if (isLiveClient && !isLiveSecret) {
      console.error('🚨 라이브 클라이언트 키와 시크릿 키가 매칭되지 않습니다');
    }
    
    if ((isTestClient || isTestSecret) && (isLiveClient || isLiveSecret)) {
      console.error('🚨 테스트 키와 라이브 키가 혼재되어 있습니다');
    }
  }

  /**
   * TossPayments SDK 초기화
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    
    try {
      // Customer key 생성
      const customerKey = this.generateCustomerKey();
      
      // Payment 인스턴스 초기화는 widgets에서만 사용
      
      // Widget 인스턴스 초기화 (결제 위젯용)
      this.widgets = await loadTossPayments(this.clientKey).then(tp => 
        tp.widgets({ customerKey })
      );
      
      this.isInitialized = true;
    } catch (error) {
      console.error('TossPayments SDK 초기화 실패:', (error as any)?.message);
      throw new Error('TossPayments 초기화에 실패했습니다');
    }
  }

  /**
   * 고객 키 생성 (로그인/비로그인 고객 구분)
   * TossPayments 요구사항: 영문 대소문자, 숫자, 특수문자 -, *, =, ., @ 사용 가능, 2-50자
   */
  private generateCustomerKey(userId?: string): string {
    if (userId) {
      // 로그인 사용자: USER_ 접두사 + userId (최대 50자 제한)
      const userKey = `USER_${userId}`;
      if (userKey.length > 50) {
        return userKey.substring(0, 50);
      }
      return userKey;
    }
    
    // 비로그인 사용자의 경우 세션 기반 키 생성
    const sessionKey = sessionStorage.getItem('toss_customer_key');
    if (sessionKey) {
      return sessionKey;
    }
    
    // 새로운 게스트 키 생성 (UUID 스타일 + 허용 문자만 사용)
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 12); // 10자리 랜덤 문자열
    const newKey = `GUEST_${timestamp}_${randomPart}`;
    
    // 최대 50자 제한 확인
    if (newKey.length > 50) {
      const truncatedKey = newKey.substring(0, 50);
      sessionStorage.setItem('toss_customer_key', truncatedKey);
      return truncatedKey;
    }
    
    sessionStorage.setItem('toss_customer_key', newKey);
    return newKey;
  }

  /**
   * 결제 위젯 렌더링
   */
  async renderPaymentWidget(
    containerId: string,
    amount: number,
    options?: {
      variantKey?: string;
    }
  ): Promise<void> {
    if (!this.widgets) {
      await this.initialize();
    }

    if (!this.widgets) {
      throw new Error('TossPayments 위젯이 초기화되지 않았습니다');
    }

    const primaryVariant = options?.variantKey ?? 'widgetEco';
    const fallbackVariant = 'DEFAULT';

    const renderWithVariant = async (variant: string) => {
      await this.widgets!.renderPaymentMethods({
        selector: `#${containerId}`,
        variantKey: variant,
      });

      await this.widgets!.renderAgreement({
        selector: `#${containerId}-agreement`,
        variantKey: variant,
      });
    };

    try {
      // 결제 금액 설정 (필수 - renderPaymentMethods 호출 전에 반드시 설정)
      await this.widgets.setAmount({
        currency: 'KRW',
        value: amount,
      });

      await renderWithVariant(primaryVariant);
    } catch (error) {
      console.error(`Failed to render payment widget with variant ${primaryVariant}:`, error);

      if (primaryVariant !== fallbackVariant) {
        try {
          console.warn(`Retrying TossPayments widget rendering with fallback variant ${fallbackVariant}`);
          await renderWithVariant(fallbackVariant);
          return;
        } catch (fallbackError) {
          console.error(`Fallback rendering with variant ${fallbackVariant} also failed:`, fallbackError);
        }
      }

      throw new Error('결제 위젯 렌더링에 실패했습니다');
    }
  }

  /**
   * 결제 금액 업데이트 (쿠폰, 할인 등으로 금액 변경 시 사용)
   */
  async updateAmount(amount: number): Promise<void> {
    if (!this.widgets) {
      throw new Error('TossPayments 위젯이 초기화되지 않았습니다');
    }

    try {
      await this.widgets.setAmount({
        currency: 'KRW',
        value: amount,
      });
    } catch (error) {
      console.error('Failed to update payment amount:', error);
      throw new Error('결제 금액 업데이트에 실패했습니다');
    }
  }

  /**
   * 결제 요청
   */
  async requestPayment(paymentRequest: PaymentRequest): Promise<void> {
    if (!this.widgets) {
      await this.initialize();
    }

    if (!this.widgets) {
      throw new Error('TossPayments 위젯이 초기화되지 않았습니다');
    }

    const { orderId, orderName, amount, customerEmail, customerName, customerMobilePhone } = paymentRequest;
    
    // 필수 파라미터 검증
    const validationErrors: string[] = [];
    
    if (!orderId || typeof orderId !== 'string') {
      validationErrors.push('orderId가 유효하지 않습니다');
    }
    
    if (!orderName || typeof orderName !== 'string') {
      validationErrors.push('orderName이 유효하지 않습니다');
    }
    
    if (!amount || typeof amount !== 'number' || amount <= 0) {
      validationErrors.push('amount가 유효하지 않습니다');
    }
    
    if (customerEmail && typeof customerEmail !== 'string') {
      validationErrors.push('customerEmail 형식이 유효하지 않습니다');
    }
    
    if (customerName && typeof customerName !== 'string') {
      validationErrors.push('customerName 형식이 유효하지 않습니다');
    }
    
    if (customerMobilePhone && typeof customerMobilePhone !== 'string') {
      validationErrors.push('customerMobilePhone 형식이 유효하지 않습니다');
    }
    
    if (validationErrors.length > 0) {
      throw new Error(`파라미터 검증 실패: ${validationErrors.join(', ')}`);
    }
    
    try {
      // 결제 요청 직전 위젯 상태 재확인
      if (!this.widgets) {
        throw new Error('결제 위젯이 초기화되지 않았습니다');
      }
      
      // 먼저 결제 금액을 설정해야 함
      await this.widgets.setAmount({
        currency: 'KRW',
        value: amount,
      });
      
      // TossPayments 위젯 API는 amount를 별도로 받지 않음 (setAmount로 이미 설정)
      const paymentParams = {
        orderId,
        orderName,
        successUrl: `${window.location.origin}/payment/success`,
        failUrl: `${window.location.origin}/payment/fail`,
        customerEmail,
        customerName,
        customerMobilePhone,
      };
      
      // 결제 요청
      await this.widgets.requestPayment(paymentParams);
    } catch (error: any) {
      // 에러 코드별 처리
      if (error.code === 'USER_CANCEL') {
        throw new Error('결제를 취소하셨습니다');
      } else if (error.code === 'INVALID_CARD_COMPANY') {
        throw new Error('유효하지 않은 카드입니다');
      } else if (error.code === 'UNKNOWN') {
        console.error('TossPayments UNKNOWN 에러:', error.message);
        throw new Error(`알 수 없는 에러가 발생했습니다. ${error.message || ''}`);
      } else {
        console.error('TossPayments 결제 요청 실패:', error.message);
        throw new Error(error.message || '결제 요청에 실패했습니다');
      }
    }
  }

  /**
   * 결제 승인 (서버 사이드에서 호출)
   * 실제로는 Edge Function에서 처리
   */
  async confirmPayment(confirmation: PaymentConfirmation): Promise<Response> {
    const { paymentKey, orderId, amount } = confirmation;
    
    try {
      // Supabase Edge Function 호출
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/confirm-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          paymentKey,
          orderId,
          amount,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '결제 승인에 실패했습니다');
      }

      return response;
    } catch (error) {
      console.error('Payment confirmation failed:', error);
      throw error;
    }
  }

  /**
   * 포인트 전액 결제(0원) 완료 처리
   */
  async completePointPayment(orderId: string): Promise<{ success: boolean; message?: string; error?: string; paymentKey?: string }> {
    const { data, error } = await supabase.functions.invoke('complete-point-payment', {
      body: { orderId }
    });

    if (error) {
      console.error('Point payment completion failed:', error);
      return { success: false, error: '포인트 결제 처리에 실패했습니다.' };
    }

    return data as { success: boolean; message?: string; error?: string; paymentKey?: string };
  }

  /**
   * 결제 취소 (서버 사이드에서 호출)
   * 실제로는 Edge Function에서 처리
   */
  async cancelPayment(cancellation: PaymentCancellation): Promise<Response> {
    const { paymentKey, cancelReason, cancelAmount } = cancellation;
    
    try {
      // Supabase Edge Function 호출
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/cancel-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          paymentKey,
          cancelReason,
          cancelAmount,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '결제 취소에 실패했습니다');
      }

      return response;
    } catch (error) {
      console.error('Payment cancellation failed:', error);
      throw error;
    }
  }

  /**
   * 결제 실패 시 장바구니 상태 복원
   * @param cartId 장바구니 ID
   * @param orderType 주문 타입
   */
  async handlePaymentFailure(cartId: string, orderType?: string): Promise<void> {
    try {
      // Import cart service dynamically to avoid circular dependency
      const { cartService } = await import('./cart');
      
      // 장바구니 상태 복원
      const restoreResult = await cartService.restoreCartOnPaymentFailure(cartId, orderType);
      
      if (!restoreResult.success) {
        console.error('Failed to restore cart state:', restoreResult.error);
      }
    } catch (error) {
      console.error('Error handling payment failure:', error);
    }
  }

  /**
   * 결제 상태 조회
   */
  async getPaymentStatus(paymentKey: string): Promise<Response> {
    try {
      const response = await fetch(`/api/payments/${paymentKey}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '결제 상태 조회에 실패했습니다');
      }

      return response;
    } catch (error) {
      console.error('Failed to get payment status:', error);
      throw error;
    }
  }

  /**
   * 부분 환불 처리
   */
  async requestRefund(refund: RefundRequest): Promise<Response> {
    try {
      const response = await fetch('/api/payments/refund', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(refund),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || '환불 처리에 실패했습니다');
      }

      return response;
    } catch (error) {
      console.error('Refund request failed:', error);
      throw error;
    }
  }

  /**
   * 결제 위젯 완전 정리
   */
  cleanup(): void {
    try {
      console.log('🧹 TossPayments 위젯 정리 시작...');
      
      // 1. 위젯 DOM 완전 정리
      const paymentContainer = document.getElementById('payment-widget-container');
      const agreementContainer = document.getElementById('payment-widget-container-agreement');
      
      if (paymentContainer) {
        paymentContainer.innerHTML = '';
        console.log('✅ 결제 위젯 컨테이너 정리 완료');
      }
      
      if (agreementContainer) {
        agreementContainer.innerHTML = '';
        console.log('✅ 약관 동의 위젯 컨테이너 정리 완료');
      }
      
      // 2. 위젯 인스턴스 정리
      if (this.widgets) {
        try {
          // 위젯에 destroy 메서드가 있다면 호출
          if (typeof (this.widgets as any).destroy === 'function') {
            (this.widgets as any).destroy();
          }
        } catch (error) {
          console.warn('위젯 destroy 중 오류 (무시됨):', error);
        }
      }
      
      this.widgets = null;
      this.isInitialized = false;
      
      console.log('🎉 TossPayments 위젯 완전 정리 완료');
      
    } catch (error) {
      console.error('위젯 정리 중 오류:', error);
      // 오류가 발생해도 기본 정리는 수행
      this.widgets = null;
      this.isInitialized = false;
    }
  }

  /**
   * 강제 위젯 재설정 (DOM 오류 발생 시 사용)
   */
  async forceReset(): Promise<void> {
    console.log('🔄 TossPayments 위젯 강제 재설정...');
    
    this.cleanup();
    
    // sessionStorage의 customer key도 정리
    sessionStorage.removeItem('toss_customer_key');
    
    // SDK 완전 정리를 위한 지연 (더 긴 시간)
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // 모든 플래그 리셋
    this.isInitialized = false;
    this.widgets = null;
    
    console.log('✅ 위젯 강제 재설정 완료 - SDK 완전 정리 대기 완료');
  }
}

// 싱글톤 인스턴스 생성 및 export
export const tossPaymentsService = new TossPaymentsService();
